import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { XCircle, CreditCard, Loader2, X } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function PagoCancelado() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [isRetrying, setIsRetrying] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [pendingData, setPendingData] = useState(null);
  const token = localStorage.getItem('clinica_token');

  useEffect(() => {
    if (sessionId && token) {
      fetchPendingData();
    }
  }, [sessionId, token]);

  const fetchPendingData = async () => {
    try {
      const response = await axios.get(`${API}/payments/pending/${sessionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPendingData(response.data);
    } catch (error) {
      console.error('Error fetching pending data:', error);
    }
  };

  const handleRetryPayment = async () => {
    if (!sessionId || !pendingData) {
      toast.error('No se encontraron los datos del pago');
      return;
    }

    setIsRetrying(true);
    try {
      // Create new checkout session with the same form data
      const response = await axios.post(
        `${API}/payments/create-stripe-checkout`,
        {
          origin_url: window.location.origin,
          form_data: pendingData.form_data,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Redirect to new Stripe Checkout
      window.location.href = response.data.url;
    } catch (error) {
      console.error('Error retrying payment:', error);
      toast.error('Error al reintentar el pago: ' + (error.response?.data?.detail || error.message));
      setIsRetrying(false);
    }
  };

  const handleCancelCheckout = async () => {
    if (!sessionId) {
      navigate('/clinica/dashboard');
      return;
    }

    setIsCancelling(true);
    try {
      await axios.delete(`${API}/payments/cancel-checkout/${sessionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Solicitud cancelada');
      navigate('/clinica/dashboard');
    } catch (error) {
      console.error('Error cancelling checkout:', error);
      // Navigate anyway
      navigate('/clinica/dashboard');
    }
  };

  return (
    <div className="min-h-screen bg-slate-100">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <img src="/amplicare-logo.svg" alt="Amplicare Lab" className="h-9" />
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
          {/* Header */}
          <div className="bg-amber-500 px-6 py-8 text-center">
            <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center mx-auto mb-4">
              <XCircle className="w-12 h-12 text-amber-500" />
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">
              Pago No Completado
            </h1>
            <p className="text-white/90">
              El proceso de pago no fue completado
            </p>
          </div>

          {/* Content */}
          <div className="p-6 space-y-6">
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 space-y-2">
              <p className="text-slate-700">
                <strong>No se ha registrado ningún cargo a su tarjeta.</strong>
              </p>
              <p className="text-slate-600">
                Para procesar su solicitud, es necesario completar el pago.
              </p>
            </div>

            {pendingData && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-800">
                  <strong>Total a pagar:</strong> ${pendingData.total?.toFixed(2)} MXN
                </p>
                <p className="text-sm text-blue-700 mt-1">
                  <strong>Paciente:</strong> {pendingData.form_data?.patient_info?.nombre || 'N/A'}
                </p>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex flex-col gap-3 pt-4">
              <Button
                onClick={handleRetryPayment}
                disabled={isRetrying || !pendingData}
                className="w-full h-12 bg-green-600 hover:bg-green-700 text-white"
              >
                {isRetrying ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Procesando...
                  </>
                ) : (
                  <>
                    <CreditCard className="w-4 h-4 mr-2" />
                    Reintentar Pago
                  </>
                )}
              </Button>
              <Button
                variant="outline"
                onClick={handleCancelCheckout}
                disabled={isCancelling}
                className="w-full h-12 border-red-300 text-red-600 hover:bg-red-50"
              >
                {isCancelling ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Cancelando...
                  </>
                ) : (
                  <>
                    <X className="w-4 h-4 mr-2" />
                    Cancelar Solicitud
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
